/**
 * useGeolocation — request browser GPS once on demand.
 *
 * Returns { coords, status, error, request } where status is one of
 * "idle" | "asking" | "granted" | "denied" | "unsupported" | "error".
 * `coords` is { latitude, longitude, accuracy } when status === "granted".
 *
 * The hook does NOT call navigator.geolocation on mount — only when
 * `request()` is invoked, so the user sees a clear cause-effect with the
 * permission prompt.
 */
import { useCallback, useState } from "react";

export default function useGeolocation() {
  const [coords, setCoords] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);

  const request = useCallback(() => {
    return new Promise((resolve) => {
      if (typeof navigator === "undefined" || !navigator.geolocation) {
        setStatus("unsupported");
        resolve(null);
        return;
      }
      setStatus("asking");
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const c = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          };
          setCoords(c);
          setStatus("granted");
          setError(null);
          resolve(c);
        },
        (err) => {
          const denied = err?.code === 1;
          setStatus(denied ? "denied" : "error");
          setError(err?.message || "GPS no disponible");
          resolve(null);
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
      );
    });
  }, []);

  return { coords, status, error, request };
}
